import { Invoice } from '../types/invoice';
import { User } from '../types';
import { generateInvoiceHTMLNew, type SupplierProfile } from './pdfGenerator';
import { getValidAuthHeadersOrThrow } from './auth';

interface EmailData {
  to: string;
  replyTo?: string;
  subject: string;
  html: string;
  attachments?: Array<{
    filename: string;
    content: string;
    contentType: string;
  }>;
}

const emailTemplates = {
  invoiceSubject: {
    en: 'Invoice #{invoiceNumber} from {companyName}',
    nl: 'Factuur #{invoiceNumber} van {companyName}'
  },
  invoiceBody: {
    en: `Dear {customerName},

Please find attached your invoice #{invoiceNumber} for €{totalAmount}.

Invoice Details:
- Invoice Number: {invoiceNumber}
- Invoice Date: {invoiceDate}
- Due Date: {dueDate}
- Total Amount: €{totalAmount}

Payment Instructions:
{paymentInstructions}

Thank you for your business!

Best regards,
{companyName}

---
This email was sent from our Professional Inventory Management System.
If you have any questions, please reply to this email.`,
    nl: `Beste {customerName},

Hierbij ontvangt u factuur #{invoiceNumber} voor €{totalAmount}.

Factuurgegevens:
- Factuurnummer: {invoiceNumber}
- Factuurdatum: {invoiceDate}
- Vervaldatum: {dueDate}
- Totaalbedrag: €{totalAmount}

Betaalinstructies:
{paymentInstructions}

Bedankt voor uw vertrouwen!

Met vriendelijke groet,
{companyName}

---
Deze email is verzonden vanuit ons Professionele Voorraadbeheersysteem.
Voor vragen kunt u reageren op deze email.`
  }
};

export class EmailService {
  private static instance: EmailService;

  static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  async sendEmail(emailData: EmailData): Promise<boolean> {
    try {
      console.log('📧 Sending email via Edge Function...');
      console.log(`To: ${emailData.to}`);
      console.log(`Subject: ${emailData.subject}`);
      console.log(`Reply-To: ${emailData.replyTo || 'Not set'}`);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      if (!supabaseUrl) {
        throw new Error('Supabase configuration not found');
      }

      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('Not authenticated — please log in again before sending email');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: await getValidAuthHeadersOrThrow(),
        body: JSON.stringify(emailData),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as Record<string, string>).details || 'Failed to send email');
      }

      await response.json();
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Email request timed out. Please try again.');
      }
      throw new Error('Failed to send email. Please try again.');
    }
  }

  async sendInvoiceEmail(
    invoice: Invoice,
    user: User,
    customerEmail: string,
    customSubject?: string,
    customMessage?: string
  ): Promise<boolean> {
    try {
      const language = invoice.language as 'en' | 'nl';

      const subject = customSubject || this.generateEmailSubject(invoice, user, language);

      const htmlBody = customMessage
        ? this.formatCustomMessage(customMessage, invoice, user)
        : this.generateEmailBody(invoice, user, language);

      const emailData: EmailData = {
        to: customerEmail,
        replyTo: user.email,
        subject,
        html: htmlBody,
        attachments: [
          {
            filename: `Invoice_${invoice.invoice_number}.html`,
            content: this.generateInvoiceHTML(invoice, user),
            contentType: 'text/html'
          }
        ]
      };

      return await this.sendEmail(emailData);
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      throw error;
    }
  }

  private generateEmailSubject(invoice: Invoice, user: User, language: 'en' | 'nl'): string {
    const template = emailTemplates.invoiceSubject[language];
    return template
      .replace('{invoiceNumber}', invoice.invoice_number)
      .replace('{companyName}', user.full_name || user.email);
  }

  private generateEmailBody(invoice: Invoice, user: User, language: 'en' | 'nl'): string {
    const template = emailTemplates.invoiceBody[language];

    const paymentInstructions = this.generatePaymentInstructions(language);

    return template
      .replace('{customerName}', invoice.customer_name)
      .replace(/{invoiceNumber}/g, invoice.invoice_number)
      .replace('{totalAmount}', invoice.total_amount.toFixed(2))
      .replace('{invoiceDate}', new Date(invoice.invoice_date).toLocaleDateString())
      .replace('{dueDate}', new Date(invoice.due_date).toLocaleDateString())
      .replace('{companyName}', user.full_name || user.email)
      .replace('{paymentInstructions}', paymentInstructions);
  }

  private formatCustomMessage(message: string, invoice: Invoice, user: User): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h2 style="color: #333; margin: 0;">Invoice ${invoice.invoice_number}</h2>
          <p style="color: #666; margin: 5px 0 0 0;">From: ${user.full_name || user.email}</p>
        </div>

        <div style="white-space: pre-line; line-height: 1.6; color: #333;">
          ${message}
        </div>

        <div style="margin-top: 30px; padding: 20px; background: #e3f2fd; border-radius: 8px;">
          <h3 style="color: #1976d2; margin: 0 0 10px 0;">Invoice Summary</h3>
          <p style="margin: 5px 0;"><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
          <p style="margin: 5px 0;"><strong>Total Amount:</strong> €${invoice.total_amount.toFixed(2)}</p>
          <p style="margin: 5px 0;"><strong>Due Date:</strong> ${new Date(invoice.due_date).toLocaleDateString()}</p>
        </div>

        <div style="margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; font-size: 12px; color: #666;">
          <p style="margin: 0;">This email was sent from our Professional Inventory Management System.</p>
          <p style="margin: 5px 0 0 0;">For questions, please reply to this email or contact: ${user.email}</p>
        </div>
      </div>
    `;
  }

  private generatePaymentInstructions(language: 'en' | 'nl'): string {
    if (language === 'nl') {
      return `Betaling binnen 14 dagen na factuurdatum.`;
    } else {
      return `Payment due within 14 days of invoice date.`;
    }
  }

  private generateInvoiceHTML(invoice: Invoice, user: User): string {
    const supplier: SupplierProfile = {
      company_name: user.company_name || user.full_name || user.email,
      legal_entity_name: user.legal_entity_name,
      company_street: user.company_street,
      company_house_number: user.company_house_number,
      company_postal_code: user.company_postal_code,
      company_city: user.company_city,
      company_country: user.company_country,
      company_phone: user.company_phone,
      company_email: user.company_email,
      company_website: user.company_website,
      company_address: user.company_address,
      kvk_number: user.kvk_number,
      vat_number: user.vat_number,
      company_tax_id: user.company_tax_id,
      iban: user.iban,
      bic: user.bic,
      bank_name: user.bank_name,
      logo_url: user.logo_url,
      payment_terms_days: user.payment_terms_days,
    };
    return generateInvoiceHTMLNew(invoice, supplier);
  }
}

export const emailService = EmailService.getInstance();

export const sendInvoiceEmail = async (
  invoice: Invoice,
  user: User,
  customerEmail: string,
  customSubject?: string,
  customMessage?: string
): Promise<boolean> => {
  return await emailService.sendInvoiceEmail(
    invoice,
    user,
    customerEmail,
    customSubject,
    customMessage
  );
};

export const checkEmailConfiguration = (): boolean => {
  return true;
};

export const sendShipmentNotification = async (
  shipmentData: {
    shipmentNumber: string;
    customerName: string;
    shippingAddress: string;
    trackingCode?: string;
    carrier?: string;
    items: Array<{
      product_name: string;
      quantity: number;
    }>;
    notes?: string;
  },
  customerEmail: string,
  senderInfo: {
    company_name: string;
    company_address?: string;
    company_email?: string;
    company_phone?: string;
    full_name?: string;
  }
): Promise<boolean> => {
  try {
    const subject = `Shipment Notification - ${shipmentData.shipmentNumber} from ${senderInfo.company_name}`;

    const trackingInfo = shipmentData.trackingCode
      ? `
        <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #1976d2; margin: 0 0 10px 0;">📦 Tracking Information</h3>
          <p style="margin: 5px 0;"><strong>Tracking Code:</strong> ${shipmentData.trackingCode}</p>
          ${shipmentData.carrier ? `<p style="margin: 5px 0;"><strong>Carrier:</strong> ${shipmentData.carrier}</p>` : ''}
          <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
            You will receive tracking updates as your shipment progresses.
          </p>
        </div>
      `
      : `
        <div style="background: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0; color: #e65100;">
            <strong>Note:</strong> Tracking code will be provided once the shipment is picked up by the carrier.
          </p>
        </div>
      `;

    const itemsTable = `
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
        <thead>
          <tr style="background: #f5f5f5;">
            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Product</th>
            <th style="border: 1px solid #ddd; padding: 12px; text-align: center; width: 100px;">Quantity</th>
          </tr>
        </thead>
        <tbody>
          ${shipmentData.items.map(item => `
            <tr>
              <td style="border: 1px solid #ddd; padding: 12px;">${item.product_name}</td>
              <td style="border: 1px solid #ddd; padding: 12px; text-align: center;">${item.quantity}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Shipment Notification</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background: white; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center;">
              <h1 style="margin: 0; font-size: 28px;">🚚 Shipment Notification</h1>
              <p style="margin: 10px 0 0 0; opacity: 0.9;">Your order is on the way!</p>
            </div>

            <!-- Main Content -->
            <div style="padding: 30px;">
              <p style="font-size: 16px; color: #333; margin: 0 0 20px 0;">
                Dear <strong>${shipmentData.customerName}</strong>,
              </p>

              <p style="color: #666; margin: 0 0 20px 0;">
                We are pleased to inform you that your shipment has been dispatched and is on its way to you.
              </p>

              <!-- Shipment Details -->
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #333; margin: 0 0 15px 0;">📋 Shipment Details</h3>
                <p style="margin: 5px 0;"><strong>Shipment Number:</strong> ${shipmentData.shipmentNumber}</p>
                <p style="margin: 5px 0;"><strong>Shipping Address:</strong></p>
                <p style="margin: 5px 0 5px 20px; color: #666;">${shipmentData.shippingAddress.replace(/\n/g, '<br>')}</p>
              </div>

              ${trackingInfo}

              <!-- Items Shipped -->
              <div style="margin: 25px 0;">
                <h3 style="color: #333; margin: 0 0 15px 0;">📦 Items Shipped</h3>
                ${itemsTable}
              </div>

              ${shipmentData.notes ? `
                <div style="background: #fffbea; padding: 15px; border-left: 4px solid #ffc107; border-radius: 4px; margin: 20px 0;">
                  <p style="margin: 0; color: #856404;"><strong>📝 Additional Notes:</strong></p>
                  <p style="margin: 10px 0 0 0; color: #856404;">${shipmentData.notes}</p>
                </div>
              ` : ''}

              <!-- Sender Information -->
              <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
                <h4 style="color: #333; margin: 0 0 10px 0;">Shipped by:</h4>
                <p style="margin: 5px 0; color: #666;"><strong>${senderInfo.company_name}</strong></p>
                ${senderInfo.company_address ? `<p style="margin: 5px 0; color: #666;">${senderInfo.company_address}</p>` : ''}
                ${senderInfo.company_phone ? `<p style="margin: 5px 0; color: #666;">📞 ${senderInfo.company_phone}</p>` : ''}
                ${senderInfo.company_email ? `<p style="margin: 5px 0; color: #666;">✉️ ${senderInfo.company_email}</p>` : ''}
              </div>
            </div>

            <!-- Footer -->
            <div style="background: #f5f5f5; padding: 20px; border-radius: 0 0 10px 10px; text-align: center;">
              <p style="margin: 0; font-size: 12px; color: #999;">
                This email was sent from Professional Inventory Management System
              </p>
              <p style="margin: 10px 0 0 0; font-size: 12px; color: #999;">
                If you have any questions, please contact us at ${senderInfo.company_email || 'the sender'}
              </p>
            </div>
          </div>
        </body>
      </html>
    `;

    const emailData: EmailData = {
      to: customerEmail,
      replyTo: senderInfo.company_email,
      subject,
      html
    };

    return await emailService.sendEmail(emailData);
  } catch (error) {
    console.error('Failed to send shipment notification:', error);
    throw error;
  }
};
